import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { FilterValue, TableFilter } from '@ipfabric/utils';
import { getOperatorAndValueFromReadable } from 'components/Table/components/SimpleFilter/helpers/getFilterFromReadable';
import type { SimpleFilterField } from '../types/SimpleFilterField';
import { asnValueConvertor } from './filterValueConvertors/asnValueConvertor';
import { measureUnitValueConvertor } from './filterValueConvertors/measureUnitValueConvertor';
import { uptimeValueConvertor } from './filterValueConvertors/uptimeValueConvertor';

export const unformatFilterValue = (
  filterField: SimpleFilterField,
  filterValue: FilterValue | FilterValue[],
): FilterValue | FilterValue[] => {
  if (filterValue === null) {
    return null;
  }

  let unformattedValue =
    typeof filterValue === 'string' ? filterValue.trim() : filterValue;

  switch (filterField.filter.type) {
    case 'select':
      // Convert filter option values to number if filter optionKeyType is number
      // API expects number values for number select filters, e.g., status column in the duplex technology table
      if (filterField.filter.optionKeyType === 'number') {
        unformattedValue = Number(filterValue.toString());
        return Number.isNaN(unformattedValue) ? filterValue : unformattedValue;
      }
      return unformattedValue;
    case 'number':
      unformattedValue = measureUnitValueConvertor.toNumber(
        filterValue.toString(),
      );
      return Number.isNaN(unformattedValue) ? filterValue : unformattedValue;
    case 'uptime':
      return uptimeValueConvertor.toNumber(filterValue.toString());
    case 'asn':
      unformattedValue = unformattedValue.toString();
      return asnValueConvertor.toNumber(unformattedValue);
    default:
      return unformattedValue;
  }
};

export const formatFilterValue = (
  value: FilterValue | FilterValue[],
  filter: TableFilter,
  oldFilterField?: SimpleFilterField,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
): string => {
  if (value === null || Array.isArray(value)) {
    return '';
  }

  switch (filter.type) {
    case 'number':
      if (typeof value !== 'number' && Number.isNaN(Number(value))) {
        stumble('Saved number filter value is not a number');
        return '';
      }
      /**
       * Allows to avoid the double convertion on filter initial values changing.
       */
      if (oldFilterField) {
        const operatorAndValue = getOperatorAndValueFromReadable(
          oldFilterField.filter,
          oldFilterField.value,
        );
        if (operatorAndValue !== null) {
          const [, oldFilterFieldValue] = operatorAndValue;
          if (
            typeof value === 'number' &&
            value.toString() === oldFilterFieldValue
          ) {
            return oldFilterFieldValue;
          }
        }
      }
      return filter.compact
        ? measureUnitValueConvertor.toMeasureUnit(Number(value))
        : value.toString();
    case 'uptime':
      if (typeof value !== 'number' && Number.isNaN(Number(value))) {
        stumble('Saved uptime filter value is not a number');
        return '';
      }
      return uptimeValueConvertor.toString(Number(value));
    case 'asn':
      if (typeof value !== 'number' && Number.isNaN(Number(value))) {
        stumble('Saved asn filter value is not a number');
        return '';
      }
      return asnValueConvertor.toString(Number(value));
    default:
      return value.toString();
  }
};
